import type { EngineLayer } from '@mighty-map/map-engine';
import type { LayerConfig, Manifest, ThemeConfig } from '../api/types';
import { effectiveVisibleLayers, flattenLayerIds, pruneTree, type VisibilityMap } from './tree';

export const MAX_ZOOM = 24;

export interface EngineLayerInput {
  theme: ThemeConfig;
  layers: ReadonlyMap<string, LayerConfig>;
  manifest: Manifest | null;
  visibility: VisibilityMap;
}

/**
 * Config + manifest + visibility -> the engine's layer set for one theme.
 * Engine order is draw order (last on top), so the panel's top layer comes last.
 * Layers with no published tiles in the manifest are left out: there is nothing to draw.
 */
export function toEngineLayers({ theme, layers, manifest, visibility }: EngineLayerInput): EngineLayer[] {
  const tree = pruneTree(theme.tree, new Set(layers.keys()));
  const visible = effectiveVisibleLayers(tree, visibility);
  const tiles = new Map((manifest?.layers ?? []).map((m) => [m.layer_id, m.tiles_url]));
  const out: EngineLayer[] = [];
  for (const id of flattenLayerIds(tree).reverse()) {
    const cfg = layers.get(id);
    const url = tiles.get(id);
    if (!cfg || !url) continue;
    out.push({
      id,
      sourceUrl: url,
      sourceLayer: id,
      styleLayers: cfg.style,
      visible: visible.has(id),
      minzoom: cfg.zoom.min,
      maxzoom: cfg.zoom.max ?? MAX_ZOOM,
    });
  }
  return out;
}
